import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, ArrowsLeftRight, Stack, MagnifyingGlassPlus, Scales,
  MagnifyingGlassMinus, ArrowCounterClockwise, X, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { drawingRevApi } from './drawingRevApi';
import { DocFileViewer } from './DocFileViewer';

const BLEND = [
  { id: 'difference', label: '差分を光らせる', hint: '同じ所は黒く沈み、変わった所だけ光ります' },
  { id: 'multiply', label: '重ねて透かす', hint: '両方の線をそのまま重ねます' },
];

const TAB = (on) => `flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
  on ? 'bg-zinc-800 text-teal-300 border border-zinc-700' : 'text-zinc-400 hover:text-zinc-100'}`;

/** 片側が画像にできなかったときの説明。原因とHTTP状態まで出す（既定文だけでは直せない）。 */
const PageError = ({ tag, err, overlay = false }) => (
  <div className={`mt-10 max-w-md text-center space-y-1.5 ${overlay ? 'text-zinc-300' : ''}`}
    data-testid={`compare-error-${tag}`}>
    <p className="text-[12px] font-semibold text-amber-400">
      {tag} の図面を画像にできませんでした
    </p>
    <p className="text-[12px] text-zinc-600 dark:text-zinc-300">{err.message}</p>
    {err.status && (
      <p className="text-[11px] font-mono text-zinc-500">
        HTTP {err.status}
        {err.status === 404 && ' — 更新直後でまだ配信されていない可能性があります。少し待って再読み込みしてください'}
        {err.status === 502 && ' — 図面ファイルの保存先（NAS・ストレージ）から読み出せませんでした'}
        {err.status === 403 && ' — このフォルダを見る権限がありません'}
        {err.status === 422 && ' — このファイルはPDF・画像として読めませんでした'}
      </p>
    )}
    {overlay && (
      <p className="text-[11px] text-zinc-500">
        重ね合わせ・差分は2枚とも画像にできないと使えません。「並べて表示」でどちらが読めないか確認できます。
      </p>
    )}
  </div>
);

/** 新旧2枚を「並べて」「重ねて」「差分だけ塗って」比べる。A=旧, B=新。 */
export function DrawingCompare({ a, b, onClose, onSwap }) {
  const [mode, setMode] = useState('side');      // side | overlay | diff
  const [blend, setBlend] = useState('difference');
  // 「差分を光らせる」は2枚の差を取る合成なので、濃さ1.0でないと
  // 同じ所が黒く沈まず、全体が灰色に濁る。既定は1.0にして必要なら下げる。
  const [opacity, setOpacity] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [big, setBig] = useState(null);          // 原寸ビューアーで開いている図面
  // A と B は別々に読む。片方が読めないときに比較画面ごと真っ暗になると、
  // どちらの図面が悪いのか分からなくなる。
  const [side, setSide] = useState({ a: null, b: null });
  const urls = useRef([]);

  // 差分ハイライト
  const [align, setAlign] = useState(true);
  const [highlight, setHighlight] = useState('marker');
  const [pair, setPair] = useState(null);
  const [pairErr, setPairErr] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);
  const [diffErr, setDiffErr] = useState(null);
  const [cursor, setCursor] = useState(-1);
  const stage = useRef(null);
  const canvasWrap = useRef(null);

  useEffect(() => {
    let alive = true;
    setSide({ a: null, b: null });
    const load = async (key, did) => {
      try {
        const url = await drawingRevApi.pageImage(did, 1.6);
        if (!alive) { URL.revokeObjectURL(url); return; }
        urls.current.push(url);
        setSide((prev) => ({ ...prev, [key]: { url } }));
      } catch (e) {
        if (!alive) return;
        setSide((prev) => ({ ...prev, [key]: { err: {
          message: e?.detail || e?.response?.data?.detail || e?.message
            || '図面を画像にできませんでした',
          status: e?.status ?? e?.response?.status ?? null,
        } } }));
      }
    };
    load('a', a.document_id);
    load('b', b.document_id);
    return () => {
      alive = false;
      urls.current.forEach((u) => URL.revokeObjectURL(u));
      urls.current = [];
    };
  }, [a.document_id, b.document_id]);

  const imgA = side.a?.url || null;
  const imgB = side.b?.url || null;
  const failed = [['A', a, side.a], ['B', b, side.b]].filter(([, , st]) => st?.err);

  // 重ねて表示も、差分ハイライトと同じ「外枠を左上で合わせる」処理を通す。
  // 画面で2枚を素朴に重ねると、用紙サイズが違う版では線が二重に見える。
  useEffect(() => {
    if (mode !== 'overlay') return undefined;
    let alive = true;
    setPair(null); setPairErr(null);
    drawingRevApi.align({ a: a.document_id, b: b.document_id, align })
      .then((d) => { if (alive) setPair(d); })
      .catch((e) => { if (alive) setPairErr(e?.response?.data?.detail || '位置合わせできませんでした'); });
    return () => { alive = false; };
  }, [mode, a.document_id, b.document_id, align]);

  useEffect(() => {
    if (mode !== 'diff') return undefined;
    let alive = true;
    setDiffBusy(true); setDiffErr(null); setDiff(null); setCursor(-1);
    drawingRevApi.diff({ a: a.document_id, b: b.document_id, align, highlight })
      .then((d) => { if (alive) setDiff(d); })
      .catch((e) => { if (alive) setDiffErr(e?.response?.data?.detail || '差分を作れませんでした'); })
      .finally(() => { if (alive) setDiffBusy(false); });
    return () => { alive = false; };
  }, [mode, a.document_id, b.document_id, align, highlight]);

  const regions = useMemo(() => diff?.regions || [], [diff]);

  const jump = useCallback((next) => {
    if (!regions.length || !stage.current || !canvasWrap.current) return;
    const i = ((next % regions.length) + regions.length) % regions.length;
    setCursor(i);
    const r = regions[i];
    const box = canvasWrap.current;
    const el = stage.current;
    el.scrollTo({
      left: (r.left + r.width / 2) * box.clientWidth - el.clientWidth / 2,
      top: (r.top + r.height / 2) * box.clientHeight - el.clientHeight / 2,
      behavior: 'smooth',
    });
  }, [regions]);

  const label = (m) => `REV ${m.rev || '—'}${m.rev_date ? ` · ${m.rev_date}` : ''}`;
  const zoomBtn = 'p-1.5 rounded hover:bg-zinc-700/60 text-zinc-300 transition-colors';
  const pct = diff ? Math.round((diff.stats.changed_ratio || 0) * 1000) / 10 : null;

  return (
    <div className="fixed inset-0 z-[120] bg-zinc-950 flex flex-col" data-testid="drawing-compare">
      <div className="shrink-0 flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-bold tracking-wider text-zinc-500">比較</span>
          <span className="text-xs font-semibold text-zinc-100 truncate">{a.drawing_no || a.title}</span>
        </div>

        <div className="flex items-center gap-1 ml-2">
          <button type="button" onClick={() => setMode('side')} data-testid="compare-mode-side"
            className={TAB(mode === 'side')}>
            <ArrowsLeftRight size={13} /> 並べて表示
          </button>
          <button type="button" onClick={() => setMode('overlay')} data-testid="compare-mode-overlay"
            className={TAB(mode === 'overlay')}>
            <Stack size={13} /> 重ねて表示
          </button>
          <button type="button" onClick={() => setMode('diff')} data-testid="compare-mode-diff"
            className={TAB(mode === 'diff')}>
            <Scales size={13} /> 差分ハイライト
          </button>
        </div>

        {mode === 'overlay' && (
          <div className="flex items-center gap-3 ml-2 pl-3 border-l border-zinc-800">
            <select value={blend} onChange={(e) => setBlend(e.target.value)}
              data-testid="compare-blend"
              className="bg-zinc-900 border border-zinc-700 rounded text-[11px] text-zinc-200 px-1.5 py-1">
              {BLEND.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={align} onChange={(e) => setAlign(e.target.checked)}
                data-testid="compare-overlay-align" className="accent-teal-500" />
              自動位置合わせ
            </label>
            <label className="flex items-center gap-2 text-[11px] text-zinc-400">
              新版の濃さ
              <input type="range" min="0" max="1" step="0.05" value={opacity}
                data-testid="compare-opacity"
                onChange={(e) => setOpacity(Number(e.target.value))} className="w-24 accent-teal-500" />
            </label>
          </div>
        )}

        {mode === 'diff' && (
          <div className="flex items-center gap-3 ml-2 pl-3 border-l border-zinc-800">
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={align} onChange={(e) => setAlign(e.target.checked)}
                data-testid="compare-diff-align" className="accent-teal-500" />
              自動位置合わせ
            </label>
            <select value={highlight} onChange={(e) => setHighlight(e.target.value)}
              data-testid="compare-diff-highlight"
              className="bg-zinc-900 border border-zinc-700 rounded text-[11px] text-zinc-200 px-1.5 py-1">
              <option value="marker">蛍光ペン（文字が読める）</option>
              <option value="both">消えた線=赤 / 増えた線=青</option>
              <option value="red">変更はすべて赤</option>
            </select>
            {regions.length > 0 && (
              <div className="flex items-center gap-1" data-testid="compare-diff-nav">
                <button type="button" className={zoomBtn} onClick={() => jump(cursor - 1)}
                  data-testid="compare-diff-prev"><CaretLeft size={13} /></button>
                <span className="text-[10px] text-zinc-400 tabular-nums">
                  変更 {cursor < 0 ? '—' : cursor + 1}/{regions.length}
                </span>
                <button type="button" className={zoomBtn} onClick={() => jump(cursor + 1)}
                  data-testid="compare-diff-next"><CaretRight size={13} /></button>
              </div>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={onSwap} data-testid="compare-swap"
            className="px-2 py-1 rounded text-[11px] text-zinc-400 hover:text-zinc-100">A/B入替</button>
          <button type="button" className={zoomBtn} onClick={() => setZoom((z) => Math.max(0.3, z - 0.2))}
            data-testid="compare-zoom-out"><MagnifyingGlassMinus size={15} /></button>
          <span className="text-[10px] text-zinc-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button type="button" className={zoomBtn} onClick={() => setZoom((z) => Math.min(4, z + 0.2))}
            data-testid="compare-zoom-in"><MagnifyingGlassPlus size={15} /></button>
          <button type="button" className={zoomBtn} onClick={() => setZoom(1)}
            data-testid="compare-zoom-reset"><ArrowCounterClockwise size={14} /></button>
          <button type="button" onClick={onClose} data-testid="compare-close"
            className="ml-1 p-1.5 rounded hover:bg-zinc-800 text-zinc-400"><X size={16} /></button>
        </div>
      </div>

      {mode === 'overlay' && (
        <p className="shrink-0 px-4 py-1 text-[10px] text-zinc-500 border-b border-zinc-800/60">
          {BLEND.find((o) => o.id === blend)?.hint}
          <span className="ml-2 text-red-400">■ 旧 {label(a)}</span>
          <span className="ml-2 text-blue-400">■ 新 {label(b)}</span>
          {pair?.align?.applied && (
            <span className="ml-auto text-zinc-400 tabular-nums">
              外枠を左上で合わせました（倍率{pair.align.scale} / {pair.align.dx},{pair.align.dy}px）
            </span>
          )}
        </p>
      )}

      {mode === 'diff' && (
        <p className="shrink-0 px-4 py-1 text-[10px] text-zinc-500 border-b border-zinc-800/60 flex items-center gap-3"
          data-testid="compare-diff-legend">
          <span className="text-zinc-400">■ 変わらない線</span>
          <span className="text-red-400">■ {highlight === 'red' ? '変わった所' : `消えた線（旧 ${label(a)}）`}</span>
          {highlight !== 'red' && <span className="text-blue-400">■ 増えた線（新 {label(b)}）</span>}
          {highlight === 'marker' && (
            <span className="text-zinc-500">変わった所の背後に色を敷いています（文字はそのまま読めます）</span>
          )}
          {diff && (
            <span className="ml-auto flex items-center gap-3 text-zinc-400 tabular-nums">
              <span data-testid="compare-diff-ratio">変更 {pct}%</span>
              <span>消えた {diff.stats.removed_pixels.toLocaleString()}px</span>
              <span>増えた {diff.stats.added_pixels.toLocaleString()}px</span>
              {diff.align.applied && (
                <span title="自動で合わせた倍率と位置">
                  合わせ 倍率{diff.align.scale} / {diff.align.dx},{diff.align.dy}px
                </span>
              )}
            </span>
          )}
        </p>
      )}

      {mode === 'side' && (
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-px bg-zinc-800">
          {[[a, imgA, 'A', 'text-red-400', side.a], [b, imgB, 'B', 'text-blue-400', side.b]].map(([m, img, tag, cls, st]) => (
            <div key={tag} className="flex flex-col min-h-0 bg-zinc-900">
              <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 border-b border-zinc-800">
                <span className={`text-[10px] font-bold ${cls}`}>{tag}</span>
                <span className="text-[11px] text-zinc-300">{label(m)}</span>
                {m.is_latest && (
                  <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold tracking-wider">最新版</span>
                )}
                <button type="button" onClick={() => setBig({
                  documentId: m.document_id,
                  filename: m.filename || m.title || `${tag}.pdf`,
                  title: `${tag} — ${label(m)}`,
                })}
                  className="ml-auto inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800"
                  data-testid={`compare-enlarge-${tag}`}>
                  <MagnifyingGlassPlus size={12} /> 原寸で開く
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-auto bg-zinc-100 flex items-start justify-center p-3">
                {img ? (
                  <img src={img} alt={tag} style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
                    onDoubleClick={() => setBig({
                      documentId: m.document_id,
                      filename: m.filename || m.title || `${tag}.pdf`,
                      title: `${tag} — ${label(m)}`,
                    })}
                    className="shadow-lg bg-white cursor-zoom-in" />
                ) : st?.err ? (
                  <PageError tag={tag} err={st.err} />
                ) : (
                  <ArrowClockwise size={26} className="animate-spin text-zinc-400 mt-16" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'overlay' && failed.length > 0 && (
        <div className="flex-1 flex items-center justify-center p-6" data-testid="compare-overlay-blocked">
          <PageError tag={failed.map(([t]) => t).join('・')} err={failed[0][2].err} overlay />
        </div>
      )}

      {mode === 'overlay' && failed.length === 0 && (
        <div className="flex-1 min-h-0 overflow-auto bg-black flex items-start justify-center p-4"
          data-testid="compare-overlay-stage">
          {pairErr && <p className="mt-20 text-[12px] text-amber-300" data-testid="compare-overlay-error">{pairErr}</p>}
          {!pairErr && !pair && (
            <div className="mt-20 text-center text-zinc-500" data-testid="compare-overlay-loading">
              <ArrowClockwise size={26} className="mx-auto animate-spin" />
              <p className="text-[11px] mt-2">{align ? '外枠を合わせています…' : '図面を並べています…'}</p>
            </div>
          )}
          {!pairErr && pair && (
            <div className="relative shadow-2xl shrink-0" style={{ width: `${zoom * 100}%` }}>
              <img src={pair.a_image} alt="A" className="w-full block bg-white" />
              <img src={pair.b_image} alt="B" style={{ opacity, mixBlendMode: blend }}
                className="absolute inset-0 w-full block" />
            </div>
          )}
        </div>
      )}

      {mode === 'diff' && (
        <div ref={stage} className="flex-1 min-h-0 overflow-auto bg-zinc-900 flex items-start justify-center p-4"
          data-testid="compare-diff-stage">
          {diffBusy && (
            <div className="mt-20 text-center text-zinc-500" data-testid="compare-diff-loading">
              <ArrowClockwise size={26} className="mx-auto animate-spin" />
              <p className="text-[11px] mt-2">{align ? '位置を合わせて差分を探しています…' : '差分を探しています…'}</p>
            </div>
          )}
          {!diffBusy && diffErr && (
            <div className="mt-20 text-[12px] text-amber-300" data-testid="compare-diff-error">{diffErr}</div>
          )}
          {!diffBusy && diff && (
            <div ref={canvasWrap} className="relative shadow-2xl shrink-0"
              style={{ width: `${zoom * 100}%` }}>
              <img src={diff.image} alt="差分" className="w-full block bg-white" data-testid="compare-diff-image" />
              {regions.map((r, i) => (
                <span key={`${r.left}-${r.top}`} aria-hidden
                  className={`absolute pointer-events-none rounded-sm transition-all duration-200 ${
                    i === cursor ? 'ring-2 ring-amber-400 shadow-[0_0_0_9999px_rgba(9,9,11,0.35)]' : 'ring-1 ring-amber-400/25'}`}
                  style={{
                    left: `${r.left * 100}%`, top: `${r.top * 100}%`,
                    width: `${r.width * 100}%`, height: `${r.height * 100}%`,
                  }} />
              ))}
              {diff.stats.identical && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="px-3 py-1.5 rounded bg-zinc-950/85 text-teal-300 text-[12px] font-semibold"
                    data-testid="compare-diff-identical">この2枚に線の違いは見つかりませんでした</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {big && (
        <DocFileViewer documentId={big.documentId} filename={big.filename}
          title={big.title} onClose={() => setBig(null)} />
      )}
    </div>
  );
}

export default DrawingCompare;
